import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import SampleProcessesModal from '@/modules/configurationModule/detail/samples/processesTypes/SampleProcessesModal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import { ConfigurationSampleProcessRequest, IContentSampleProcessesGrid, initialSampleProcessesGridState } from '@/modules/configurationModule/detail/samples/processesTypes/SampleProcesses.models'
import { sampleProcessesConfigurationServices } from '@/api/resources/configuration/samples/sampleProcesses/sampleProcessServices'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import {
    ConfigurationSampleProcessPatchInBatchRequest
} from '@/modules/configurationModule/detail/automation/grossing-shelf/intermediate-shelf-process/services/configuration-sample-process/configuration-sample-process.dto'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        SampleProcessesModal,
        CodificationSearcherModal
    }
})
export default class SampleProcessesGrid extends BaseCrudServerSideComponent<IContentSampleProcessesGrid> implements BaseCrud {
    @Ref() readonly sampleProcessesModalWrapper!: any
    @Ref() readonly codificationSearcherModalWrapper!: any

    constructor() {
        super()
        this.state = initialSampleProcessesGridState()
    }

    showCodificationSearcherModal() {
        this.codificationSearcherModalWrapper.show()
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:IContentSampleProcessesGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.sampleProcessesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async createOperation(data: IContentSampleProcessesGrid) {
        const sampleProcessToCreate: ConfigurationSampleProcessRequest = {
            active: data?.active,
            code: data?.code!,
            nameTranslations: data.nameTranslations.map(nameTranslation => {
                return {
                    value: nameTranslation.value,
                    notes: nameTranslation.notes,
                    languageId: nameTranslation.language.id
                }
            })
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleProcessesConfigurationServices.createSampleProcesses(sampleProcessToCreate))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.sampleProcessesModalWrapper.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(data: IContentSampleProcessesGrid[]) {
        const sampleProcessToPatch: ConfigurationSampleProcessPatchInBatchRequest[] = data.map((itemToPatch: any) => {
            return {
                active: itemToPatch?.active,
                code: itemToPatch?.code,
                id: itemToPatch?.id as number,
                nameTranslations: itemToPatch.nameTranslations.map((lang: any) => {
                    return {
                        value: lang.value,
                        notes: lang.notes,
                        languageId: lang.language.id
                    }
                })
            }
        })
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleProcessesConfigurationServices.patchSampleProcessesBatch(sampleProcessToPatch))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.sampleProcessesModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: IContentSampleProcessesGrid[]) {
        const sampleProcessesToPatch = selectionBatch?.filter((sampleProcesses: IContentSampleProcessesGrid) => sampleProcesses.active !== option)?.map((sampleProcesses:IContentSampleProcessesGrid) => {
            return {
                id: sampleProcesses.id as number,
                active: option
            }
        })
        if (sampleProcessesToPatch.length) {
            const errors = await this.$_toCallUnique(sampleProcessesConfigurationServices.patchSampleProcessesBatch(sampleProcessesToPatch))
            if (!errors?.length) {
                if (selectionBatch.length > 1) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
                } else {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
                }
            }
            this.reloadData()
        }
    }

    async deleteBatchOperation(sampleProcesseses: IContentSampleProcessesGrid[]) {
        const sampleProcessesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: sampleProcesseses.map((sampleProcesses: IContentSampleProcessesGrid) => Number(sampleProcesses?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleProcessesConfigurationServices.deleteSampleProcessesBatch(sampleProcessesToDelete))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (sampleProcesseses.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: sampleProcesseses?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(sampleProcesses: IContentSampleProcessesGrid[]) {
        if (sampleProcesses.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                await this.deleteBatchOperation(sampleProcesses)
            }
        }
    }
}
